import React, { useContext, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  PlusIcon,
  StackIcon,
  InfoIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DimensionType,
  DIMENSION_LABELS,
  DimensionGroupingConfig,
  MultiYearForecastData,
  TableMetric,
  TABLE_METRIC_LABELS,
  DEFAULT_TABLE_METRIC,
} from '@/types/rebateAnalytics';
import { UnifiedForecastResult } from '@erp/shared-types';
import { useBreakdownCells } from './hooks/useBreakdownCells';
import BreakdownTable, {
  type BreakdownCellContext,
} from '../common/breakdown-tables/BreakdownTable';
import {
  createDataCellRenderer,
  aggregateCellValuesWithYOY,
} from './utils/rebateAnalyticsUtils';
import { createMonthMetadata } from '@/utils/rebateForecastingUtils';
import {
  generateMonthColumnOrder,
  generateMonthSubColumnOrder,
} from '@/utils/breakdown-transforms/monthOrdering';
import { DimensionGroupingModal } from './DimensionGroupingModal';
import { T, Var, useGT } from 'gt-react';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';

interface RebateAnalyticsTableConfigFormProps {
  primaryXDimension: DimensionType;
  secondaryXDimension: DimensionType | undefined;
  primaryYDimension: DimensionType | undefined;
  secondaryYDimension: DimensionType | undefined;
  onSecondaryXDimensionChange: (value: DimensionType | undefined) => void;
  onPrimaryYDimensionChange: (value: DimensionType | undefined) => void;
  onSecondaryYDimensionChange: (value: DimensionType | undefined) => void;
  metric?: TableMetric;
  onMetricChange?: (value: TableMetric) => void;
  autoGeneratedName: string;
  customName?: string;
  onCustomNameChange?: (name: string) => void;
  isValidName?: boolean;
  hasDuplicates: boolean;
  showPreview?: boolean;
  forecastData: UnifiedForecastResult | null;
  multiYearForecastData?: MultiYearForecastData;
  supplierIdToName?: Map<string, string>;
  divisionIdToName?: Map<string, string>;
  ruleTagIdToName?: Map<string, string>;
  agreementTagIdToName?: Map<string, string>;
  supplierTagIdToName?: Map<string, string>;
  buttonLabel?: string;
  buttonIcon?: React.ReactNode;
  onSave: () => void;
  onCancel?: () => void;
  // Dimension grouping props (supports all dimensions)
  ruleIdToName?: Map<string, string>;
  agreementIdToName?: Map<string, string>;
  dimensionGroupingConfig?: Partial<
    Record<
      Exclude<DimensionType, DimensionType.TIME | DimensionType.RULE_TYPE>,
      DimensionGroupingConfig
    >
  >;
  onDimensionGroupingConfigChange?: (
    dimensionType: Exclude<
      DimensionType,
      DimensionType.TIME | DimensionType.RULE_TYPE
    >,
    config: DimensionGroupingConfig | undefined,
  ) => void;
}

/**
 * Reusable form fields component for table configuration
 */
function RebateAnalyticsTableConfigForm({
  primaryXDimension,
  secondaryXDimension,
  primaryYDimension,
  secondaryYDimension,
  onSecondaryXDimensionChange,
  onPrimaryYDimensionChange,
  onSecondaryYDimensionChange,
  metric = DEFAULT_TABLE_METRIC,
  onMetricChange,
  autoGeneratedName,
  customName,
  onCustomNameChange,
  isValidName = true,
  hasDuplicates,
  showPreview = false,
  forecastData,
  multiYearForecastData,
  supplierIdToName,
  divisionIdToName,
  ruleTagIdToName,
  agreementTagIdToName,
  supplierTagIdToName,
  buttonLabel = 'Create',
  buttonIcon = <PlusIcon className="h-4 w-4" />,
  onSave,
  onCancel,
  ruleIdToName,
  agreementIdToName,
  dimensionGroupingConfig,
  onDimensionGroupingConfigChange,
}: RebateAnalyticsTableConfigFormProps) {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const userIsAdmin = isAdmin(currentUser, viewMode);

  // Affected Volume can n-count across overlapping rules and its forecast
  // run-rate can diverge from invoice Total Volume — admin-only for now.
  // Keep it visible when already selected so existing tables remain editable.
  const availableMetrics = useMemo(() => {
    return (Object.keys(TABLE_METRIC_LABELS) as TableMetric[]).filter(
      (metricOption) =>
        metricOption !== 'affectedVolume' ||
        userIsAdmin ||
        metric === 'affectedVolume',
    );
  }, [userIsAdmin, metric]);

  // Modal state for dimension grouping editors - one per dimension type
  // RULE_TYPE is excluded because it doesn't support grouping
  const [openModalForDimension, setOpenModalForDimension] = useState<Exclude<
    DimensionType,
    DimensionType.TIME | DimensionType.RULE_TYPE
  > | null>(null);
  // Get all dimensions except TIME (Month)
  const allDimensionsExceptTime = Object.values(DimensionType).filter(
    (dim) => dim !== DimensionType.TIME,
  );

  // Helper function to check if a dimension is already used (duplicate)
  const isDimensionUsed = (dimension: DimensionType): boolean => {
    const selectedDimensions = [
      primaryXDimension,
      secondaryXDimension,
      primaryYDimension,
      secondaryYDimension,
    ].filter((d): d is DimensionType => d !== undefined);

    return selectedDimensions.filter((d) => d === dimension).length > 1;
  };

  // Helper function to check if a dimension is selected elsewhere
  const isDimensionSelectedElsewhere = (
    dimension: DimensionType,
    currentSelector: 'secondaryX' | 'primaryY' | 'secondaryY',
  ): boolean => {
    const otherSelections: DimensionType[] = [];
    if (currentSelector !== 'secondaryX') {
      if (secondaryXDimension) otherSelections.push(secondaryXDimension);
    }
    if (currentSelector !== 'primaryY') {
      if (primaryYDimension) otherSelections.push(primaryYDimension);
    }
    if (currentSelector !== 'secondaryY') {
      if (secondaryYDimension) otherSelections.push(secondaryYDimension);
    }
    // Always include primaryX (TIME) but it's locked so not a duplicate concern

    return otherSelections.includes(dimension);
  };

  // Generate preview data if needed (hooks must be called unconditionally)
  const previewData = useBreakdownCells({
    multiYearForecastData: multiYearForecastData || {
      currentYear: new Date().getFullYear(),
      currentYearData: null,
      previousYearData: null,
      isCumulative: false,
    },
    primaryXDimension,
    secondaryXDimension,
    primaryYDimension,
    secondaryYDimension,
    dimensionGroupingConfig,
    metric,
  });

  // Create month metadata from forecast data for cell renderer
  const monthMetadata = useMemo(() => {
    if (!multiYearForecastData?.currentYearData) {
      return [];
    }
    return createMonthMetadata(multiYearForecastData.currentYearData);
  }, [multiYearForecastData]);

  // Create cell renderer adapter
  const previewRenderDataCell = useMemo(() => {
    if (!showPreview || previewData.length === 0) return undefined;
    const currentYear =
      multiYearForecastData?.currentYear || new Date().getFullYear();
    const previousYear = currentYear - 1;
    const originalRenderer = createDataCellRenderer(
      monthMetadata,
      currentYear,
      previousYear,
    );

    return (context: BreakdownCellContext) => {
      const { cell } = context;
      const metadata = cell.metadata as
        | {
            headerName?: string;
            isGroupLevel?: boolean;
          }
        | undefined;

      // Create mock old-style context for compatibility
      const mockOldContext = {
        header: {
          name: metadata?.headerName || cell.column,
        },
        row: metadata?.isGroupLevel
          ? undefined
          : {
              label: cell.subRow || '',
              values: { [metadata?.headerName || '']: cell.value },
            },
        group: {
          label: cell.row,
          values: metadata?.isGroupLevel
            ? { [metadata.headerName || '']: cell.value }
            : undefined,
        },
        isGroupLevel: metadata?.isGroupLevel ?? false,
      };

      const legacyValue = cell.value as
        number | { current: number; previous: number; delta: number } | null;
      return originalRenderer(legacyValue, mockOldContext);
    };
  }, [monthMetadata, showPreview, previewData, multiYearForecastData]);

  // Get dimension labels for preview
  const previewGroupLabel = primaryYDimension
    ? DIMENSION_LABELS[primaryYDimension]
    : undefined;
  const previewSubRowLabel = secondaryYDimension
    ? DIMENSION_LABELS[secondaryYDimension]
    : undefined;

  // Generate month ordering if primary X dimension is TIME
  const xDimOrder = useMemo(() => {
    if (!showPreview || previewData.length === 0) return undefined;
    if (primaryXDimension === DimensionType.TIME) {
      return generateMonthColumnOrder(previewData);
    }
    return undefined;
  }, [primaryXDimension, previewData, showPreview]);

  const xDim2Order = useMemo(() => {
    if (!showPreview || previewData.length === 0) return undefined;
    if (secondaryXDimension === DimensionType.TIME) {
      return generateMonthSubColumnOrder(previewData);
    }
    return undefined;
  }, [secondaryXDimension, previewData, showPreview]);

  // Compute initial expanded rows/columns for preview
  const initialExpandedRows = useMemo(() => {
    if (!showPreview || previewData.length === 0) return new Set<string>();
    // Get first unique row value
    const firstRow = previewData.find((c) => c.subRow === null)?.row;
    if (firstRow) {
      return new Set([firstRow]);
    }
    return new Set<string>();
  }, [previewData, showPreview]);

  const initialExpandedColumns = useMemo(() => {
    if (!showPreview || !secondaryXDimension || previewData.length === 0) {
      return new Set<string>();
    }
    // Get first unique column value
    const firstColumn = previewData.find((c) => c.subColumn === null)?.column;
    if (firstColumn) {
      return new Set([firstColumn]);
    }
    return new Set<string>();
  }, [showPreview, secondaryXDimension, previewData]);

  // Check if we're in the default state (only TIME selected, all others are defaults/none)
  const isDefaultState = useMemo(() => {
    return (
      secondaryXDimension === undefined &&
      primaryYDimension === undefined &&
      secondaryYDimension === undefined
    );
  }, [secondaryXDimension, primaryYDimension, secondaryYDimension]);

  // Derived: check if name is custom
  const isCustomName = useMemo(() => {
    return customName !== undefined && customName !== autoGeneratedName;
  }, [customName, autoGeneratedName]);

  // Check if reset button should be shown
  // Show whenever name is custom and differs from auto-generated (regardless of dimension changes)
  const showResetButton = useMemo(() => {
    return (
      isCustomName &&
      customName !== undefined &&
      customName.trim() !== '' &&
      customName !== autoGeneratedName
    );
  }, [isCustomName, customName, autoGeneratedName]);

  // Handle reset to auto-generated name
  const handleResetToAutoGen = () => {
    if (onCustomNameChange) {
      onCustomNameChange(autoGeneratedName);
    }
  };

  // Check if form is valid (primaryYDimension is required and name is valid)
  const isFormValid = useMemo(() => {
    return !hasDuplicates && primaryYDimension !== undefined && isValidName;
  }, [hasDuplicates, primaryYDimension, isValidName]);

  const duplicateDimensionLabels = useMemo(() => {
    if (!hasDuplicates) {
      return '';
    }
    return [
      primaryXDimension,
      secondaryXDimension,
      primaryYDimension,
      secondaryYDimension,
    ]
      .filter((d): d is DimensionType => d !== undefined)
      .reduce((dups, dim, idx, arr) => {
        if (arr.indexOf(dim) !== idx && !dups.includes(dim)) {
          dups.push(dim);
        }
        return dups;
      }, [] as DimensionType[])
      .map((d) => DIMENSION_LABELS[d])
      .join(', ');
  }, [
    hasDuplicates,
    primaryXDimension,
    secondaryXDimension,
    primaryYDimension,
    secondaryYDimension,
  ]);

  const noneOptionalLabel = gt('None (optional)', {
    context: 'rebate analytics table: optional dimension select placeholder',
  });
  const noneRequiredLabel = gt('None (Required)', {
    context: 'rebate analytics table: required row dimension placeholder',
  });

  return (
    <div className="space-y-4 w-full min-w-0 max-w-full">
      {/* Table Name - full width */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          <T context="rebate analytics table config: name field label">
            Table Name
          </T>
        </Label>
        <Input
          value={customName ?? autoGeneratedName}
          onChange={(e) => {
            if (onCustomNameChange) {
              onCustomNameChange(e.target.value);
            }
          }}
          placeholder={autoGeneratedName}
          className={cn(
            'w-full',
            !isValidName && 'border-destructive focus-visible:ring-destructive',
          )}
        />
        {!isValidName && (
          <p className="text-sm text-destructive">
            <T context="rebate analytics table config: validation message for empty name">
              Table name cannot be empty or contain only whitespace
            </T>
          </p>
        )}
        {showResetButton && (
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={handleResetToAutoGen}
            className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
          >
            <T context="rebate analytics table config: reset name to auto-generated">
              Use: <Var>{autoGeneratedName}</Var>
            </T>
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 space-y-3">
          {/* Metric Row */}
          {onMetricChange && (
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 w-30">
                  <Label className="text-sm font-medium">
                    <T context="rebate analytics table config: cell value metric label">
                      Value:
                    </T>
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-medium mb-1">
                        <T context="rebate analytics table config: cell value metric tooltip title">
                          Cell Value
                        </T>
                      </p>
                      <p className="text-xs">
                        {userIsAdmin ? (
                          <T context="rebate analytics table config: cell value metric tooltip body admin">
                            Rebate Amount shows rebate dollars. Total Volume
                            shows invoice purchase volume by vendor/month;
                            months after the last entered data are run-rate
                            projected. Affected Volume shows the purchase volume
                            each rebate rule applies to — it can double-count
                            when multiple rules affect the same products. All
                            metrics respect the Cumulative / Month by Month
                            toggle. Rule dimensions are not meaningful for Total
                            Volume.
                          </T>
                        ) : (
                          <T context="rebate analytics table config: cell value metric tooltip body">
                            Rebate Amount shows rebate dollars. Total Volume
                            shows invoice purchase volume by vendor/month;
                            months after the last entered data are run-rate
                            projected. All metrics respect the Cumulative /
                            Month by Month toggle. Rule dimensions are not
                            meaningful for Total Volume.
                          </T>
                        )}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Select
                  value={metric}
                  onValueChange={(value) => {
                    if (
                      value === 'rebateAmount' ||
                      value === 'totalVolume' ||
                      (value === 'affectedVolume' && userIsAdmin)
                    ) {
                      onMetricChange(value);
                    }
                  }}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMetrics.map((metricOption) => (
                      <SelectItem key={metricOption} value={metricOption}>
                        {TABLE_METRIC_LABELS[metricOption]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* X Axis Row */}
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 w-30">
                <Label className="text-sm font-medium">
                  <T context="rebate analytics table config: column axis label">
                    Column Axis:
                  </T>
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-medium mb-1">
                      <T context="rebate analytics table config: column axis tooltip title">
                        Column Axis (X)
                      </T>
                    </p>
                    <p className="text-xs">
                      <T context="rebate analytics table config: column axis tooltip body">
                        Columns display Time (required) and optionally a second
                        dimension for nested columns. For example: Time ×
                        Division creates columns grouped by month, then
                        subdivided by division.
                      </T>
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Select value={primaryXDimension} disabled>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DimensionType.TIME}>
                    {DIMENSION_LABELS[DimensionType.TIME]}
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="h-7 w-6"></div>
              <span className="text-muted-foreground text-sm">×</span>
              <div className="flex items-center gap-2">
                <Select
                  value={secondaryXDimension || 'none'}
                  onValueChange={(value) =>
                    onSecondaryXDimensionChange(
                      value === 'none' ? undefined : (value as DimensionType),
                    )
                  }
                >
                  <SelectTrigger
                    className={cn(
                      'w-[160px]',
                      secondaryXDimension &&
                        isDimensionUsed(secondaryXDimension) &&
                        'border-destructive text-destructive',
                    )}
                  >
                    <SelectValue placeholder={noneOptionalLabel} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{noneOptionalLabel}</SelectItem>
                    {allDimensionsExceptTime.map((dim) => (
                      <SelectItem
                        key={dim}
                        value={dim}
                        className={
                          isDimensionSelectedElsewhere(dim, 'secondaryX')
                            ? 'text-destructive'
                            : ''
                        }
                      >
                        {DIMENSION_LABELS[dim]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {secondaryXDimension &&
                secondaryXDimension !== DimensionType.TIME &&
                secondaryXDimension !== DimensionType.RULE_TYPE &&
                onDimensionGroupingConfigChange ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setOpenModalForDimension(secondaryXDimension);
                        }}
                      >
                        <StackIcon
                          className={cn(
                            'h-3.5 w-3.5',
                            dimensionGroupingConfig?.[secondaryXDimension] &&
                              'text-purple-600',
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {dimensionGroupingConfig?.[secondaryXDimension]
                          ? gt('Edit {dimension} grouping', {
                              context:
                                'rebate analytics: dimension grouping stack button tooltip',
                              dimension: DIMENSION_LABELS[secondaryXDimension],
                            })
                          : gt('Configure {dimension} grouping', {
                              context:
                                'rebate analytics: dimension grouping stack button tooltip',
                              dimension: DIMENSION_LABELS[secondaryXDimension],
                            })}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="h-7 w-7"></div>
                )}
              </div>
            </div>
            {secondaryXDimension ? (
              <p className="text-xs text-muted-foreground italic pl-34">
                <T context="rebate analytics table config: column axis summary with nested dimension">
                  → Columns: <Var>{DIMENSION_LABELS[DimensionType.TIME]}</Var> ×{' '}
                  <Var>{DIMENSION_LABELS[secondaryXDimension]}</Var>
                </T>
              </p>
            ) : (
              <p className="text-xs text-muted-foreground italic pl-34">
                <T context="rebate analytics table config: column axis summary time only">
                  → Columns: <Var>{DIMENSION_LABELS[DimensionType.TIME]}</Var>
                </T>
              </p>
            )}
          </div>

          {/* Y Axis Row */}
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 w-30">
                <Label className="text-sm font-medium">
                  <T context="rebate analytics table config: row axis label">
                    Row Axis:
                  </T>
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-medium mb-1">
                      <T context="rebate analytics table config: row axis tooltip title">
                        Row Axis (Y)
                      </T>
                    </p>
                    <p className="text-xs">
                      <T context="rebate analytics table config: row axis tooltip body">
                        Rows require a primary dimension (e.g., Division,
                        Supplier). Add a secondary dimension for sub-rows within
                        each group. For example: Division × Supplier creates
                        rows grouped by division, with suppliers as sub-rows.
                      </T>
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={primaryYDimension || 'none'}
                  onValueChange={(value) =>
                    onPrimaryYDimensionChange(
                      value === 'none' ? undefined : (value as DimensionType),
                    )
                  }
                >
                  <SelectTrigger
                    className={cn(
                      'w-[160px]',
                      primaryYDimension &&
                        isDimensionUsed(primaryYDimension) &&
                        'border-destructive text-destructive',
                    )}
                  >
                    <SelectValue placeholder={noneRequiredLabel} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{noneRequiredLabel}</SelectItem>
                    {allDimensionsExceptTime.map((dim) => (
                      <SelectItem
                        key={dim}
                        value={dim}
                        className={
                          isDimensionSelectedElsewhere(dim, 'primaryY')
                            ? 'text-destructive'
                            : ''
                        }
                      >
                        {DIMENSION_LABELS[dim]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {primaryYDimension &&
                primaryYDimension !== DimensionType.TIME &&
                primaryYDimension !== DimensionType.RULE_TYPE &&
                onDimensionGroupingConfigChange ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setOpenModalForDimension(primaryYDimension);
                        }}
                      >
                        <StackIcon
                          className={cn(
                            'h-3.5 w-3.5',
                            dimensionGroupingConfig?.[primaryYDimension] &&
                              'text-purple-600',
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {dimensionGroupingConfig?.[primaryYDimension]
                          ? gt('Edit {dimension} grouping', {
                              context:
                                'rebate analytics: dimension grouping stack button tooltip',
                              dimension: DIMENSION_LABELS[primaryYDimension],
                            })
                          : gt('Configure {dimension} grouping', {
                              context:
                                'rebate analytics: dimension grouping stack button tooltip',
                              dimension: DIMENSION_LABELS[primaryYDimension],
                            })}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="h-7 w-7"></div>
                )}
              </div>
              <span className="text-muted-foreground text-sm">×</span>
              <div className="flex items-center gap-2">
                <Select
                  value={secondaryYDimension || 'none'}
                  onValueChange={(value) =>
                    onSecondaryYDimensionChange(
                      value === 'none' ? undefined : (value as DimensionType),
                    )
                  }
                  disabled={!primaryYDimension}
                >
                  <SelectTrigger
                    className={cn(
                      'w-[160px]',
                      secondaryYDimension &&
                        isDimensionUsed(secondaryYDimension) &&
                        'border-destructive text-destructive',
                      !primaryYDimension && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    <SelectValue placeholder={noneOptionalLabel} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{noneOptionalLabel}</SelectItem>
                    {allDimensionsExceptTime.map((dim) => (
                      <SelectItem
                        key={dim}
                        value={dim}
                        className={
                          isDimensionSelectedElsewhere(dim, 'secondaryY')
                            ? 'text-destructive'
                            : ''
                        }
                      >
                        {DIMENSION_LABELS[dim]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {secondaryYDimension &&
                secondaryYDimension !== DimensionType.TIME &&
                secondaryYDimension !== DimensionType.RULE_TYPE &&
                onDimensionGroupingConfigChange ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setOpenModalForDimension(secondaryYDimension);
                        }}
                      >
                        <StackIcon
                          className={cn(
                            'h-3.5 w-3.5',
                            dimensionGroupingConfig?.[secondaryYDimension] &&
                              'text-purple-600',
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {dimensionGroupingConfig?.[secondaryYDimension]
                          ? gt('Edit {dimension} grouping', {
                              context:
                                'rebate analytics: dimension grouping stack button tooltip',
                              dimension: DIMENSION_LABELS[secondaryYDimension],
                            })
                          : gt('Configure {dimension} grouping', {
                              context:
                                'rebate analytics: dimension grouping stack button tooltip',
                              dimension: DIMENSION_LABELS[secondaryYDimension],
                            })}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="h-7 w-7"></div>
                )}
              </div>
            </div>
            {primaryYDimension && (
              <p className="text-xs text-muted-foreground italic pl-34">
                {secondaryYDimension ? (
                  <T context="rebate analytics table config: row axis summary with sub-row">
                    → Rows: <Var>{DIMENSION_LABELS[primaryYDimension]}</Var> ×{' '}
                    <Var>{DIMENSION_LABELS[secondaryYDimension]}</Var>
                  </T>
                ) : (
                  <T context="rebate analytics table config: row axis summary primary only">
                    → Rows: <Var>{DIMENSION_LABELS[primaryYDimension]}</Var>
                  </T>
                )}
              </p>
            )}
          </div>

          {/* Dimension Grouping Info Box */}
          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 p-2.5 rounded-md border border-border/50">
            <StackIcon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-purple-600" />
            <div className="space-y-0.5">
              <p className="font-medium text-foreground">
                <T context="rebate analytics table config: dimension grouping info title">
                  Dimension Grouping
                </T>
              </p>
              <p>
                <T context="rebate analytics table config: dimension grouping info body">
                  Click the{' '}
                  <StackIcon className="h-3 w-3 inline text-purple-600" /> icon
                  next to a dimension to organize items into custom groups. For
                  example, group multiple suppliers into &quot;Tier 1
                  Suppliers&quot; or &quot;Strategic Partners&quot;.
                </T>
              </p>
            </div>
          </div>

          {/* Error message for duplicates */}
          {hasDuplicates && (
            <AlertContainer variant="destructive" className="py-2">
              <WarningCircleIcon className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <T context="rebate analytics table config: duplicate dimensions error">
                  Duplicate dimensions detected:{' '}
                  <Var>{duplicateDimensionLabels}</Var>. Each dimension can only
                  be used once.
                </T>
              </AlertDescription>
            </AlertContainer>
          )}

          {/* Dimension Grouping Modal */}
          <DimensionGroupingModal
            open={!!openModalForDimension}
            onOpenChange={(open) => {
              if (!open) {
                setOpenModalForDimension(null);
              }
            }}
            dimensionType={openModalForDimension}
            dimensionGroupingConfig={dimensionGroupingConfig}
            onDimensionGroupingConfigChange={onDimensionGroupingConfigChange}
            supplierIdToName={supplierIdToName}
            divisionIdToName={divisionIdToName}
            ruleIdToName={ruleIdToName}
            agreementIdToName={agreementIdToName}
            ruleTagIdToName={ruleTagIdToName}
            agreementTagIdToName={agreementTagIdToName}
            supplierTagIdToName={supplierTagIdToName}
          />
        </div>

        {/* Action Button */}
        <div className="flex items-start gap-3">
          <div className="flex gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button
                    size="sm"
                    onClick={onSave}
                    className="gap-2"
                    disabled={!isFormValid}
                  >
                    {buttonIcon}
                    {buttonLabel}
                  </Button>
                </span>
              </TooltipTrigger>
              {!isFormValid && (
                <TooltipContent>
                  <p>
                    {!primaryYDimension
                      ? gt('Please select a row axis to create the table', {
                          context:
                            'rebate analytics table config: save disabled tooltip',
                        })
                      : hasDuplicates
                        ? gt('Please remove duplicate dimensions', {
                            context:
                              'rebate analytics table config: save disabled tooltip',
                          })
                        : ''}
                  </p>
                </TooltipContent>
              )}
            </Tooltip>
            {onCancel && (
              <Button size="sm" variant="outline" onClick={onCancel}>
                {gt('Cancel', {
                  context: 'rebate analytics table config form dismiss',
                })}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Preview Section */}
      {showPreview &&
        !hasDuplicates &&
        !isDefaultState &&
        forecastData &&
        previewData.length > 0 &&
        previewRenderDataCell && (
          <div className="mt-4 pt-4 border-t">
            <Label className="text-sm font-medium text-muted-foreground mb-2 block">
              <T context="rebate analytics table config: preview section label">
                Preview
              </T>
            </Label>
            <div className="opacity-60 pointer-events-none max-w-full max-h-[800px] overflow-hidden">
              <BreakdownTable
                data={previewData}
                rowLabel={previewGroupLabel}
                subRowLabel={previewSubRowLabel}
                columnLabel={DIMENSION_LABELS[primaryXDimension]}
                subColumnLabel={
                  secondaryXDimension
                    ? DIMENSION_LABELS[secondaryXDimension]
                    : undefined
                }
                title=""
                subtitle=""
                showCard={false}
                renderCell={previewRenderDataCell}
                aggregateCellValues={aggregateCellValuesWithYOY}
                showXDim1Total={true}
                showYDim1Total={true}
                maxHeight={400}
                initialExpandedRows={initialExpandedRows}
                initialExpandedColumns={initialExpandedColumns}
                xDimOrder={xDimOrder}
                xDim2Order={xDim2Order}
              />
            </div>
          </div>
        )}
    </div>
  );
}

export default RebateAnalyticsTableConfigForm;
